import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { CheckIcon, FileTextIcon, PenToolIcon, SearchIcon, TargetIcon, TrendingUpIcon, UsersIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
export function ContentService() {
  const features = [{
    icon: PenToolIcon,
    title: 'Website Copywriting',
    description: 'Engaging, conversion-focused content for all your pages'
  }, {
    icon: FileTextIcon,
    title: 'Blog Posts',
    description: 'SEO-optimized articles that attract and engage readers'
  }, {
    icon: SearchIcon,
    title: 'SEO Content',
    description: 'Keyword-rich content that ranks well in search engines'
  }, {
    icon: UsersIcon,
    title: 'Social Media',
    description: 'Compelling posts that drive engagement and shares'
  }, {
    icon: TargetIcon,
    title: 'Product Descriptions',
    description: 'Persuasive copy that converts browsers into buyers'
  }, {
    icon: TrendingUpIcon,
    title: 'Email Campaigns',
    description: 'Newsletter and promotional content that drives action'
  }];
  const benefits = ['Save time by outsourcing content creation', 'Improve SEO rankings with quality content', 'Engage your audience with professional copy', 'Increase conversions with persuasive messaging', 'Maintain consistent brand voice across platforms', 'Focus on your business while we handle the writing'];
  const process = [{
    step: '01',
    title: 'Discovery Call',
    description: 'We learn about your business, target audience, brand voice, and content goals.'
  }, {
    step: '02',
    title: 'Research & Planning',
    description: 'We research your industry, competitors, and keywords to create a content strategy.'
  }, {
    step: '03',
    title: 'Content Creation',
    description: 'Our writers create high-quality, engaging content tailored to your needs and audience.'
  }, {
    step: '04',
    title: 'Review & Revisions',
    description: 'You review the content and request any changes. We refine until you are completely satisfied.'
  }, {
    step: '05',
    title: 'Delivery & Publishing',
    description: 'We deliver the final content and can help with publishing to your website or platforms.'
  }];
  const packages = [{
    name: 'Starter Content',
    price: '£250',
    features: ['Up to 5 pages of website copy', 'SEO keyword research', 'Basic brand voice guide', '2 rounds of revisions', '5-7 day turnaround']
  }, {
    name: 'Growth Content',
    price: '£500',
    features: ['Up to 10 pages of website copy', 'Advanced SEO optimization', '4 blog posts (500-800 words)', 'Detailed brand voice guide', '3 rounds of revisions', '7-10 day turnaround'],
    popular: true
  }, {
    name: 'Enterprise Content',
    price: '£1,000+',
    features: ['Unlimited pages of website copy', 'Comprehensive content strategy', '8+ blog posts per month', 'Social media content', 'Email campaigns', 'Unlimited revisions', 'Priority turnaround']
  }];
  return <>
      <Helmet data-id="element-1781">
        <title data-id="element-1782">
          Content Writing Services UK | Website Copy & Blog Posts – Bright
          Pixxel
        </title>
        <meta name="description" content="Professional content writing services for websites, blogs, and marketing. SEO-optimized copy that converts from £250." data-id="element-1783" />
      </Helmet>
      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-12 bg-gradient-to-b from-[#0C1B26] to-[#212C34]" data-id="element-1784">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1785">
          <div className="max-w-4xl mx-auto text-center" data-id="element-1786">
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.5
          }} className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#0DC3DC]/20 flex items-center justify-center" data-id="element-1787">
              <PenToolIcon size={40} className="text-[#0DC3DC]" data-id="element-1788" />
            </motion.div>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-3xl md:text-5xl font-bold mb-6 text-white" data-id="element-1789">
              Content Writing Services
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="text-lg md:text-xl text-white/90 mb-8" data-id="element-1790">
              Professional, engaging content that connects with your audience
              and drives results. From website copy to blog posts, we create
              content that works.
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1791">
              <Link to="/contact" className="bg-[#0DC3DC] hover:bg-[#0DC3DC]/90 text-white px-8 py-3 rounded-lg font-medium transition-colors shadow-lg" data-id="element-1792">
                Get a Quote
              </Link>
              <Link to="/portfolio" className="bg-white/10 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-[#0C1B26] px-8 py-3 rounded-lg font-medium transition-colors" data-id="element-1793">
                View Samples
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
      {/* Features Grid */}
      <section className="py-16 bg-white dark:bg-[#1E1E1E]" data-id="element-1794">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1795">
          <div className="max-w-6xl mx-auto" data-id="element-1796">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-center mb-12" data-id="element-1797">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-1798">
                Content Writing Services
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300" data-id="element-1799">
                Professional content for every aspect of your business
              </p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1800">
              {features.map((feature, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="bg-[#F9FAFB] dark:bg-[#212C34] p-6 rounded-xl hover:shadow-lg transition-shadow" data-id="element-1801">
                  <div className="w-12 h-12 bg-[#0DC3DC]/20 rounded-lg flex items-center justify-center mb-4" data-id="element-1802">
                    <feature.icon size={24} className="text-[#0DC3DC]" data-id="element-1803" />
                  </div>
                  <h3 className="text-xl font-bold mb-2" data-id="element-1804">{feature.title}</h3>
                  <p className="text-gray-600 dark:text-gray-300" data-id="element-1805">
                    {feature.description}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>
      {/* Benefits Section with Image */}
      <section className="py-16 bg-[#F9FAFB] dark:bg-[#121212]" data-id="element-1806">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1807">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-1808">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-1809">
              <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-1810">
                Why Professional Content Matters
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-8" data-id="element-1811">
                Quality content is the foundation of successful digital
                marketing. It helps you connect with your audience, build trust,
                and drive conversions.
              </p>
              <div className="space-y-4" data-id="element-1812">
                {benefits.map((benefit, index) => <motion.div key={index} initial={{
                opacity: 0,
                x: -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.5,
                delay: index * 0.1
              }} className="flex items-center gap-3" data-id="element-1813">
                    <div className="w-6 h-6 rounded-full bg-[#0DC3DC]/20 flex items-center justify-center flex-shrink-0" data-id="element-1814">
                      <CheckIcon size={14} className="text-[#0DC3DC]" data-id="element-1815" />
                    </div>
                    <span className="text-gray-700 dark:text-gray-300" data-id="element-1816">
                      {benefit}
                    </span>
                  </motion.div>)}
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="rounded-xl overflow-hidden shadow-xl" data-id="element-1817">
              <img src="https://images.unsplash.com/photo-1455390582262-044cdead277a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt="Professional content writer working on engaging blog posts and website copy - Bright Pixxel Studios content writing services" className="w-full h-auto" loading="lazy" decoding="async" data-id="element-1818" />
            </motion.div>
          </div>
        </div>
      </section>
      {/* Process Section */}
      <section className="py-16 bg-white dark:bg-[#1E1E1E]" data-id="element-1819">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1820">
          <div className="max-w-4xl mx-auto" data-id="element-1821">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-3xl md:text-4xl font-bold mb-12 text-center" data-id="element-1822">
              Our Content Creation Process
            </motion.h2>
            <div className="space-y-8" data-id="element-1823">
              {process.map((item, index) => <motion.div key={index} initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="flex gap-6" data-id="element-1824">
                  <div className="flex-shrink-0" data-id="element-1825">
                    <div className="w-16 h-16 rounded-full bg-[#0DC3DC]/20 flex items-center justify-center" data-id="element-1826">
                      <span className="text-2xl font-bold text-[#0DC3DC]" data-id="element-1827">
                        {item.step}
                      </span>
                    </div>
                  </div>
                  <div className="flex-grow" data-id="element-1828">
                    <h3 className="text-xl font-bold mb-2" data-id="element-1829">{item.title}</h3>
                    <p className="text-gray-600 dark:text-gray-300" data-id="element-1830">
                      {item.description}
                    </p>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>
      {/* Packages Section */}
      <section className="py-16 bg-[#F9FAFB] dark:bg-[#121212]" data-id="element-1831">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1832">
          <div className="max-w-6xl mx-auto" data-id="element-1833">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-3xl md:text-4xl font-bold mb-12 text-center" data-id="element-1834">
              Content Writing Packages
            </motion.h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1835">
              {packages.map((pkg, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className={`bg-white dark:bg-[#1E1E1E] p-8 rounded-xl shadow-md ${pkg.popular ? 'ring-2 ring-[#0DC3DC]' : ''}`} data-id="element-1836">
                  {pkg.popular && <div className="text-center mb-4" data-id="element-1837">
                      <span className="bg-[#0DC3DC] text-white px-4 py-1 rounded-full text-sm font-medium" data-id="element-1838">
                        Most Popular
                      </span>
                    </div>}
                  <h3 className="text-2xl font-bold mb-2" data-id="element-1839">{pkg.name}</h3>
                  <div className="text-3xl font-bold text-[#0DC3DC] mb-6" data-id="element-1840">
                    {pkg.price}
                  </div>
                  <ul className="space-y-3" data-id="element-1841">
                    {pkg.features.map((feature, fIndex) => <li key={fIndex} className="flex items-start gap-3" data-id="element-1842">
                        <CheckIcon size={18} className="text-[#0DC3DC] mt-0.5 flex-shrink-0" data-id="element-1843" />
                        <span className="text-gray-700 dark:text-gray-300" data-id="element-1844">
                          {feature}
                        </span>
                      </li>)}
                  </ul>
                  <Link to="/contact" className={`mt-8 block text-center px-6 py-3 rounded-lg font-medium transition-colors ${pkg.popular ? 'bg-[#0DC3DC] text-white hover:bg-[#0DC3DC]/90' : 'bg-white dark:bg-[#1E1E1E] border-2 border-[#0DC3DC] text-[#0DC3DC] hover:bg-[#0DC3DC] hover:text-white'}`} data-id="element-1845">
                    Get Started
                  </Link>
                </motion.div>)}
            </div>
            <div className="mt-8 text-center" data-id="element-1846">
              <p className="text-gray-600 dark:text-gray-300" data-id="element-1847">
                Need a custom package? We can create a tailored solution for
                your specific needs.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* Stats Section */}
      <section className="py-16 bg-white dark:bg-[#1E1E1E]" data-id="element-1848">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1849">
          <div className="max-w-5xl mx-auto" data-id="element-1850">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-3xl md:text-4xl font-bold mb-12 text-center" data-id="element-1851">
              Content That Delivers Results
            </motion.h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1852">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="text-center" data-id="element-1853">
                <div className="text-5xl font-bold text-[#0DC3DC] mb-2" data-id="element-1854">
                  +150%
                </div>
                <p className="text-gray-600 dark:text-gray-300" data-id="element-1855">
                  Average increase in organic traffic
                </p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.2
            }} className="text-center" data-id="element-1856">
                <div className="text-5xl font-bold text-[#0DC3DC] mb-2" data-id="element-1857">
                  +85%
                </div>
                <p className="text-gray-600 dark:text-gray-300" data-id="element-1858">
                  Improvement in engagement rates
                </p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.3
            }} className="text-center" data-id="element-1859">
                <div className="text-5xl font-bold text-[#0DC3DC] mb-2" data-id="element-1860">
                  +120%
                </div>
                <p className="text-gray-600 dark:text-gray-300" data-id="element-1861">
                  Increase in conversion rates
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-b from-[#0C1B26] to-[#212C34]" data-id="element-1862">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1863">
          <div className="max-w-3xl mx-auto text-center" data-id="element-1864">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-3xl md:text-4xl font-bold mb-6 text-white" data-id="element-1865">
              Ready to Elevate Your Content?
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-lg text-white/90 mb-8" data-id="element-1866">
              Get in touch today to discuss your content needs. We'll create
              engaging, SEO-optimized content that resonates with your audience
              and drives results.
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} data-id="element-1867">
              <a href="mailto:info@bright-pixxel.com?subject=Content%20Writing%20Enquiry" className="inline-block bg-[#0DC3DC] hover:bg-[#0DC3DC]/90 text-white px-8 py-4 rounded-lg font-bold transition-colors shadow-lg text-lg" data-id="element-1868">
                Request a Quote
              </a>
            </motion.div>
          </div>
        </div>
      </section>
    </>;
}